import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  TextField,
  Chip,
  Slider,
  Typography,
} from "@mui/material";
import Header from "./Header";
import Right from "./Right";
import { server } from "../../server";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyProfile } from "../../redux/myProfileSlice";
import { maritalStatusList } from "../../json/maritalStatusList";
import { dietList } from "../../json/dietList";
import { religionList } from "../../json/religionList";
import { communityList } from "../../json/communityList";
import { motherTongueList } from "../../json/motherTongueList";
import { qualificationList } from "../../json/qualificationList";
import { workingInList } from "../../json/workingInList";
import { professionList } from "../../json/professionList";
import { stateCityList } from "../../json/stateCityList";

const toOptions = (list) => list.map((i) => i.Name);

const inchesToFeet = (inches) => {
  const feet = Math.floor(inches / 12);
  const inch = inches % 12;
  return `${feet}'${inch}"`;
};

const normalizeSelection = (values) => {
  if (!values || values.length === 0) return [];
  if (values.includes("Open to All")) return ["Open to All"];
  return values;
};

const MultiSelect = ({ label, options, value, onChange }) => (
  <Autocomplete
    multiple
    options={["Open to All", ...options]}
    value={value}
    onChange={(e, v) => onChange(v)}
    renderTags={(value, getTagProps) =>
      value.map((option, index) => (
        <Chip label={option} {...getTagProps({ index })} />
      ))
    }
    renderInput={(params) => (
      <TextField {...params} label={label} size="small" />
    )}
  />
);

/* ---------- component ---------- */

export default function PartnerPreferences() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.myProfile);

  const [saving, setSaving] = useState(false);

  const [preferences, setPreferences] = useState({
    minAge: 21,
    maxAge: 35,
    minHeight: 48,
    maxHeight: 84,
    maritalStatus: ["Open to All"],
    diet: ["Open to All"],
    religion: ["Open to All"],
    community: ["Open to All"],
    motherTongue: ["Open to All"],
    state: ["Open to All"],
    city: ["Open to All"],
    qualification: ["Open to All"],
    workingIn: ["Open to All"],
    profession: ["Open to All"],
    annualIncome: ["Open to All"],
    familyType: ["Open to All"],
  });

  /* ---------- load profile ---------- */
  useEffect(() => {
    dispatch(fetchMyProfile());
  }, [dispatch]);

  /* ---------- populate from API ---------- */
  useEffect(() => {
    if (!profile?.preferences) return;
    setPreferences((prev) => ({ ...prev, ...profile.preferences }));
  }, [profile]);

  /* ---------- update ---------- */
  const updatePref = (key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: Array.isArray(value) ? normalizeSelection(value) : value,
    }));
  };

  /* ---------- save ---------- */
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        mobile: profile.mobile,
        preferences,
      };

      const res = await fetch(`${server}/profile/EditProfile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to update preferences");

      await res.json();
      dispatch(fetchMyProfile());
      alert("Partner preferences updated");
      navigate(-1);
    } catch (err) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- options ---------- */
  const stateOptions = [...new Set(stateCityList.map((i) => i.State))];
  const cityOptions = preferences.state.includes("Open to All")
    ? []
    : stateCityList
        .filter((i) => preferences.state.includes(i.State))
        .map((i) => i.City);

  /* ---------- UI ---------- */
  return (
    <>
      <Header />
      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              <div className="d-flex align-items-center mb-3">
                <Link
                  onClick={() => navigate(-1)}
                  className="icon-shape me-3 icon-lg bg-light-red rounded-circle"
                >
                  <i className="bi bi-chevron-left fs-3 text-primary"></i>
                </Link>
                <div>
                  <h3 className="fw-medium ls-sm text-dark lh-1 fs-4 m-0">
                    Partner Preferences
                  </h3>
                  <p className="lh-sm fw-normal m-0">
                    Update your partner preference details
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-4 p-5">
                <div className="row g-3">
                  {/* Age */}
                  <div className="col-lg-6 col-12 pe-lg-3">
                    <Typography gutterBottom>
                      <p className="fs-5 lh-sm text-dark">
                        Age Range
                        <br /> {preferences.minAge} - {preferences.maxAge} years
                      </p>
                    </Typography>

                    <Slider
                      value={[preferences.minAge, preferences.maxAge]}
                      min={18}
                      max={45}
                      onChange={(e, newValue) => {
                        updatePref("minAge", newValue[0]);
                        updatePref("maxAge", newValue[1]);
                      }}
                    />
                  </div>

                  {/* Height */}
                  <div className="col-lg-6 col-12 ps-lg-3">
                    <Typography gutterBottom>
                      <p className="fs-5 lh-sm text-dark">
                        Height Range
                        <br />
                        {inchesToFeet(preferences.minHeight)} -{" "}
                        {inchesToFeet(preferences.maxHeight)}
                      </p>
                    </Typography>

                    <Slider
                      value={[preferences.minHeight, preferences.maxHeight]}
                      min={48}
                      max={84}
                      onChange={(e, newValue) => {
                        updatePref("minHeight", newValue[0]);
                        updatePref("maxHeight", newValue[1]);
                      }}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Marital Status"
                      options={toOptions(maritalStatusList)}
                      value={preferences.maritalStatus}
                      onChange={(v) => updatePref("maritalStatus", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Diet"
                      options={toOptions(dietList)}
                      value={preferences.diet}
                      onChange={(v) => updatePref("diet", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Religion"
                      options={toOptions(religionList)}
                      value={preferences.religion}
                      onChange={(v) => updatePref("religion", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Community"
                      options={toOptions(communityList)}
                      value={preferences.community}
                      onChange={(v) => updatePref("community", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Mother Tongue"
                      options={toOptions(motherTongueList)}
                      value={preferences.motherTongue}
                      onChange={(v) => updatePref("motherTongue", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Qualification"
                      options={toOptions(qualificationList)}
                      value={preferences.qualification}
                      onChange={(v) => updatePref("qualification", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Working In"
                      options={toOptions(workingInList)}
                      value={preferences.workingIn}
                      onChange={(v) => updatePref("workingIn", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="Profession"
                      options={toOptions(professionList)}
                      value={preferences.profession}
                      onChange={(v) => updatePref("profession", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="State"
                      options={stateOptions}
                      value={preferences.state}
                      onChange={(v) => updatePref("state", v)}
                    />
                  </div>
                  <div className="col-lg-6 col-12">
                    <MultiSelect
                      label="City"
                      options={cityOptions}
                      value={preferences.city}
                      onChange={(v) => updatePref("city", v)}
                    />
                  </div>
                </div>

                <div>
                  <span
                    className="btn btn-danger rounded-pill fw-normal px-6 mt-4 mb-2"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving Changes" : "Update Preferences"}
                  </span>
                </div>
              </div>
            </div>

            <Right />
          </div>
        </div>
      </section>
    </>
  );
}
